import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { REQUISITION_STATUSES, PRIORITY_LEVELS } from "../../../constants/inventory";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, IconButton, Snackbar, Alert,
  Skeleton,
} from "@mui/material";
import { MdVisibility } from "react-icons/md";

const FULFILLMENT_STATUS_OPTIONS = ["approved", "processing", "partially_fulfilled"];

const Fulfillment = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchRequisitions = async () => {
    setLoading(true);
    try {
      const statuses = statusFilter ? [statusFilter] : FULFILLMENT_STATUS_OPTIONS;
      const requests = statuses.map((s) =>
        fetch(`/api/getAllRequisitions?page=${page + 1}&limit=${rowsPerPage}&status=${s}`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then((r) => r.json())
      );

      const results = await Promise.all(requests);

      if (statusFilter) {
        const data = results[0];
        setRequisitions(data.requisitions || []);
        setTotalCount(data.totalCount || 0);
      } else {
        const allRequisitions = results.flatMap((r) => r.requisitions || []);
        const uniqueMap = new Map();
        allRequisitions.forEach((req) => {
          if (!uniqueMap.has(req._id)) {
            uniqueMap.set(req._id, req);
          }
        });
        const uniqueRequisitions = Array.from(uniqueMap.values());
        uniqueRequisitions.sort((a, b) => {
          const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
          return (priorityOrder[a.priority] ?? 4) - (priorityOrder[b.priority] ?? 4);
        });
        setRequisitions(uniqueRequisitions.slice(page * rowsPerPage, (page + 1) * rowsPerPage));
        setTotalCount(uniqueRequisitions.length);
      }
    } catch {
      setSnackbar({ open: true, message: "Failed to fetch requisitions", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequisitions();
  }, [page, rowsPerPage, statusFilter]);

  const getStatusBadge = (status) => {
    const config = REQUISITION_STATUSES[status] || { label: status, color: "bg-gray-100 text-gray-800" };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = PRIORITY_LEVELS.find((p) => p.id === priority);
    if (!config) return priority;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.name}
      </span>
    );
  };

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      {/* Header */}
      <div className="flex justify-between items-center px-4 mb-4 bg-white shadow rounded-lg h-[70px]">
        <h2 className="text-xl font-semibold text-gray-800">Fulfillment</h2>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All Fulfillment Statuses</option>
          {FULFILLMENT_STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{REQUISITION_STATUSES[s]?.label || s}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                <TableCell sx={{ fontWeight: 600 }}>Req #</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Requested By</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Items</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Est. Cost (Nu)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(7)].map((_, j) => (
                      <TableCell key={j}><Skeleton animation="wave" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : requisitions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <p className="text-gray-400">No requisitions ready for fulfillment</p>
                  </TableCell>
                </TableRow>
              ) : (
                requisitions.map((req) => (
                  <TableRow
                    key={req._id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/Requisitions/View/${req._id}`)}
                  >
                    <TableCell>
                      <span className="font-medium text-primary">{req.requisitionNumber}</span>
                    </TableCell>
                    <TableCell>{req.requestedBy?.name || "N/A"}</TableCell>
                    <TableCell>{req.items?.length || 0} item(s)</TableCell>
                    <TableCell>{getPriorityBadge(req.priority)}</TableCell>
                    <TableCell>
                      {req.totalEstimatedCost?.toLocaleString() || "0"}
                    </TableCell>
                    <TableCell>{getStatusBadge(req.status)}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/Requisitions/View/${req._id}`)}
                      >
                        <MdVisibility className="text-primary" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Fulfillment;
